import { useState } from 'react';
import { ContractPaymentDetailType } from '@/enums/enums';
import { useMyDataStatus } from '@/features/aade-mydata/aade-mydata.queries';
import { getSettingsOptions } from '@/features/settings/settings.queries';
import { getUserOptions } from '@/features/user/user.queries';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { Settings } from 'lucide-react';
import { z } from 'zod';

import { Access } from '@/config/access.config';
import { Button } from '@/components/ui/button';
import { checkAccess } from '@/components/guards/check-access';

import ActivationModal from './-activation-modal';
import InvoiceCreateModal from './-invoice-create-modal';
import MyDataDocumentsTable from './-my-data-documents-table';
import MyDataDocumentSheet from './-my-data-document-sheet';
import SetupModal from './-setup-modal';

const aadeMyDataSearchSchema = z.object({
  documentId: z.number().optional(),
  documentMode: z.enum(['details', 'root']).optional(),
});

export const Route = createFileRoute('/_app/(aade-mydata)/aade-mydata/')({
  validateSearch: zodSearchValidator(aadeMyDataSearchSchema),
  component: MyDataPage,
  beforeLoad: ({ context }) => {
    const settings = context.queryClient.getQueryData(getSettingsOptions.queryKey);
    const userData = context.queryClient.getQueryData(getUserOptions.queryKey);

    if (!checkAccess(settings, userData?.user, Access.AadeMyData)) {
      throw redirect({ to: '/' });
    }
  },
});

function MyDataPage() {
  const { data, isLoading, isError } = useMyDataStatus();
  const navigate = useNavigate();

  const [showSetup, setShowSetup] = useState(false);
  const [showActivate, setShowActivate] = useState(false);
  const [showIssue, setShowIssue] = useState(false);

  if (isLoading) {
    return (
      <div className='p-6'>
        <div className='animate-pulse'>Loading MyData...</div>
      </div>
    );
  }

  if (isError) {
    return <div className='p-6 text-red-600'>Αποτυχία φόρτωσης στοιχείων MyData.</div>;
  }

  // 🔵 CASE 1: Δεν έχει γίνει setup
  if (!data?.exists) {
    return (
      <>
        <div className='p-6'>
          <h1 className='text-2xl font-bold'>AADE / MyData</h1>
          <p className='mt-2 text-muted-foreground'>Δεν έχει ολοκληρωθεί η αρχική ρύθμιση.</p>

          <button
            onClick={() => setShowSetup(true)}
            className='mt-4 rounded bg-blue-600 px-4 py-2 text-white'
          >
            Ρύθμιση MyData
          </button>
        </div>

        <SetupModal open={showSetup} onClose={() => setShowSetup(false)} />
      </>
    );
  }

  // 🔵 CASE 2 & 3: Υπάρχει setup
  return (
    <div className='space-y-6 p-6'>
      {/* Header */}
      <div className='flex flex-row justify-between py-4'>
        <div className='flex flex-col'>
          <h1 className='text-2xl font-bold'>AADE / MyData</h1>
          <p className='text-sm text-muted-foreground'>
            Διαχείριση διαβίβασης παραστατικών στην ΑΑΔΕ
          </p>
        </div>

        <Button
          variant='outline'
          className='flex items-center gap-3'
          onClick={() => navigate({ to: '/aade-mydata/settings' })}
        >
          <Settings className='h-4 w-4' />
          Στοιχεία εταιρείας
        </Button>
      </div>

      {/* Status Section */}
      <div className='flex items-center gap-6 rounded-lg bg-muted/30 p-4'>
        <div>
          <span className='font-medium'>Κατάσταση:</span>

          {data?.isActivated ? (
            <span className='ml-2 font-semibold text-green-600'>Ενεργοποιημένο</span>
          ) : (
            <span className='ml-2 font-semibold text-yellow-600'>Έτοιμο για ενεργοποίηση</span>
          )}
        </div>

        {!data?.isActivated && (
          <button
            onClick={() => setShowActivate(true)}
            className='rounded bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700'
          >
            Ενεργοποίηση
          </button>
        )}

        {data?.isActivated && (
          <button
            onClick={() => setShowIssue(true)}
            className='rounded bg-green-600 px-4 py-2 text-white transition hover:bg-green-700'
          >
            Έκδοση Απόδειξης
          </button>
        )}
      </div>

      {/* Documents */}
      {data?.isActivated && <MyDataDocumentsTable />}

      {/* Document Sheet (URL-driven) */}
      <MyDataDocumentSheet />

      {/* Modals */}
      <ActivationModal open={showActivate} onClose={() => setShowActivate(false)} />

      <InvoiceCreateModal
        open={showIssue}
        onClose={() => setShowIssue(false)}
        initAmount={0}
        paymentType={ContractPaymentDetailType.Cash}
        paymentId={null}
      />
    </div>
  );
}
